/**
 * Dashboard — the "My Maps" list. Ports mockups/02-dashboard.html to a live
 * page: loads the map list from GET /api/maps, supports card/list view, search,
 * and client-filter pills, and creates a new map (POST /api/maps) then navigates
 * into the editor.
 *
 * Data shape (list card shape):
 *   { id, name, client_id, client_name, basemap, thumbnail_key,
 *     group_count, area_count, dataset_count, group_colors: [hex…],
 *     updated_at, updated_by }
 * The list stays light (no full groups/areas) but carries the aggregates the
 * mock-up needs: the "N groups · M areas · K datasets" meta line and the
 * per-group legend-dot swatches (group_colors, in group display order).
 *
 * Search + client filter are applied client-side over the already-loaded list
 * for snappy interaction; the API also accepts ?q= & ?client_id= for future
 * server-side filtering if the list grows large.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  List as ListIcon,
  Search,
  Plus,
  Pencil,
  Trash2,
  Map as MapIcon,
} from 'lucide-react';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import { relativeTime, formatNumber } from '../utils/format';
import { useToast } from '../context/ToastContext';
import './Dashboard.css';

// Cap how many legend swatches render before collapsing into a "+N" chip, so a
// map with many groups doesn't overflow the card footer / table cell.
const MAX_LEGEND_DOTS = 6;

// Sentinel client-filter values that aren't real client names.
const ALL_CLIENTS = '__all__';
const UNASSIGNED = '__unassigned__';

export default function Dashboard() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [view, setView] = useState('cards'); // 'cards' | 'list'
  const [query, setQuery] = useState('');
  const [clientFilter, setClientFilter] = useState(ALL_CLIENTS);
  const [creating, setCreating] = useState(false);

  // Load the list once on mount.
  useEffect(() => {
    let cancelled = false;
    api
      .get('/maps')
      .then((data) => {
        if (cancelled) return;
        setMaps(Array.isArray(data) ? data : []);
        setLoadError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.message || 'Could not load maps.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Distinct client names present in the list → the filter pills. "Unassigned"
  // is added only if at least one map has no client.
  const clientPills = useMemo(() => {
    const names = new Set();
    let hasUnassigned = false;
    for (const m of maps) {
      if (m.client_name) names.add(m.client_name);
      else hasUnassigned = true;
    }
    const pills = [{ key: ALL_CLIENTS, label: 'All clients' }];
    for (const name of [...names].sort()) pills.push({ key: name, label: name });
    if (hasUnassigned) pills.push({ key: UNASSIGNED, label: 'Unassigned' });
    return pills;
  }, [maps]);

  // Apply search + client filter to the loaded list.
  const visibleMaps = useMemo(() => {
    const q = query.trim().toLowerCase();
    return maps.filter((m) => {
      if (clientFilter === UNASSIGNED && m.client_name) return false;
      if (
        clientFilter !== ALL_CLIENTS &&
        clientFilter !== UNASSIGNED &&
        m.client_name !== clientFilter
      ) {
        return false;
      }
      if (q && !(m.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [maps, query, clientFilter]);

  // Create a new (empty) map and jump straight into the editor for it.
  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const created = await api.post('/maps', { name: 'Untitled map' });
      navigate(`/maps/${created.id}`);
    } catch (err) {
      showToast(err.message || 'Could not create map.', { variant: 'error' });
      setCreating(false);
    }
  };

  // Delete a map after confirmation, then drop it from local state.
  const handleDelete = async (map, e) => {
    e.stopPropagation();
    const ok = window.confirm(`Delete "${map.name}"? This can't be undone.`);
    if (!ok) return;
    try {
      await api.delete(`/maps/${map.id}`);
      setMaps((prev) => prev.filter((m) => m.id !== map.id));
      showToast(`Deleted "${map.name}"`);
    } catch (err) {
      showToast(err.message || 'Could not delete map.', { variant: 'error' });
    }
  };

  // Rename inline via a prompt (a lightweight stand-in; the editor has the
  // richer rename UI). Persists with a minimal PUT-less approach is not possible
  // here since the list endpoint is read-only — so renaming navigates users into
  // the editor instead, where autosave handles the change.
  const handleRename = (map, e) => {
    e.stopPropagation();
    navigate(`/maps/${map.id}`);
  };

  const openMap = (map) => navigate(`/maps/${map.id}`);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main">
        <div className="page">
          <div className="page-header-row">
            <header className="app-page-header" style={{ border: 'none', padding: 0, margin: 0 }}>
              <h1 className="app-page-header-title">My Maps</h1>
              <p className="app-page-header-subtitle">
                Custom territory and targeting maps, saved with their data sets
              </p>
            </header>
            <div className="page-actions">
              <div className="segmented" role="group" aria-label="View">
                <button
                  className={`segmented-option${view === 'cards' ? ' is-active' : ''}`}
                  onClick={() => setView('cards')}
                  title="Card view"
                  aria-pressed={view === 'cards'}
                >
                  <LayoutGrid size={14} />
                </button>
                <button
                  className={`segmented-option${view === 'list' ? ' is-active' : ''}`}
                  onClick={() => setView('list')}
                  title="List view"
                  aria-pressed={view === 'list'}
                >
                  <ListIcon size={14} />
                </button>
              </div>
              <div className="search-wrap">
                <Search />
                <input
                  className="input"
                  type="text"
                  placeholder="Search maps…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
                <Plus size={14} /> New Map
              </button>
            </div>
          </div>

          <div className="pills-row">
            {clientPills.map((pill) => (
              <button
                key={pill.key}
                className={`pill${clientFilter === pill.key ? ' is-active' : ''}`}
                onClick={() => setClientFilter(pill.key)}
              >
                {pill.label}
              </button>
            ))}
          </div>

          {loading && <DashboardSkeleton />}

          {!loading && loadError && (
            <div className="callout callout-error" role="alert">
              <div>
                <div className="callout-title">Couldn’t load your maps</div>
                {loadError}
              </div>
            </div>
          )}

          {!loading && !loadError && view === 'cards' && (
            <CardGrid
              maps={visibleMaps}
              onOpen={openMap}
              onRename={handleRename}
              onDelete={handleDelete}
              onCreate={handleCreate}
              creating={creating}
              filtered={maps.length > 0 && visibleMaps.length === 0}
            />
          )}

          {!loading && !loadError && view === 'list' && (
            <MapsTable
              maps={visibleMaps}
              onOpen={openMap}
              onRename={handleRename}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Card grid view ─────────────────────────────────────────────────────────
function CardGrid({ maps, onOpen, onRename, onDelete, onCreate, creating, filtered }) {
  return (
    <div className="maps-grid">
      {maps.map((map) => (
        <div
          key={map.id}
          className="card card-clickable map-card"
          onClick={() => onOpen(map)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(map);
            }
          }}
        >
          <MapThumb map={map} />
          <div className="map-card-body">
            <div className="map-name-row">
              <h2 className="map-name">{map.name || 'Untitled map'}</h2>
              <div className="card-actions">
                <button title="Rename" onClick={(e) => onRename(map, e)}>
                  <Pencil size={13} />
                </button>
                <button className="danger" title="Delete" onClick={(e) => onDelete(map, e)}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
            <MapMeta map={map} />
            <div className="map-card-footer">
              <LegendDots colors={map.group_colors} className="legend-dots" />
              {map.client_name ? (
                <span className="badge">{map.client_name}</span>
              ) : (
                <span className="badge">Unassigned</span>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* "Create a new map" tile always trails the grid, matching the mock-up. */}
      <button className="new-map-card" onClick={onCreate} disabled={creating}>
        <Plus size={22} />
        Create a new map
      </button>

      {filtered && (
        <div className="maps-empty-inline">No maps match the current filter.</div>
      )}
    </div>
  );
}

// Card meta line: "N groups · M areas · K datasets · Edited …" (mock-up). Each
// count is pluralized; the datasets segment is omitted when a map links none, so
// the common no-dataset case reads "3 groups · 214 areas · Edited 2h ago" rather
// than "· 0 datasets ·".
function MapMeta({ map }) {
  const groups = map.group_count ?? 0;
  const areas = map.area_count ?? 0;
  const datasets = map.dataset_count ?? 0;

  const segments = [
    `${groups} ${groups === 1 ? 'group' : 'groups'}`,
    `${formatNumber(areas)} ${areas === 1 ? 'area' : 'areas'}`,
  ];
  if (datasets > 0) {
    segments.push(`${datasets} ${datasets === 1 ? 'dataset' : 'datasets'}`);
  }
  segments.push(map.updated_at ? `Edited ${relativeTime(map.updated_at)}` : 'Not edited yet');

  return (
    <div className="map-meta">
      {segments.map((seg, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="dot">•</span>}
          {seg}
        </React.Fragment>
      ))}
    </div>
  );
}

// Legend swatches for a map's group colors (group_colors, in display order).
// Renders up to MAX_LEGEND_DOTS colored dots, then a "+N" chip for the rest.
// Renders nothing when a map has no colored groups. `className` picks the size
// variant (.legend-dots on cards, .mini-dots inline in the table).
function LegendDots({ colors, className }) {
  if (!Array.isArray(colors) || colors.length === 0) return null;
  const shown = colors.slice(0, MAX_LEGEND_DOTS);
  const overflow = colors.length - shown.length;
  return (
    <span className={className}>
      {shown.map((color, i) => (
        <span key={i} style={{ background: color }} />
      ))}
      {overflow > 0 && <span className="legend-more">+{overflow}</span>}
    </span>
  );
}

// Thumbnail: the stored PNG if the map has one (client-side capture, decision 5),
// otherwise a neutral placeholder tile. The thumbnail endpoint serves what the
// client uploaded on save.
function MapThumb({ map }) {
  if (map.thumbnail_key) {
    return (
      <img
        className="map-thumb"
        src={`/api/maps/${map.id}/thumbnail`}
        alt=""
        loading="lazy"
      />
    );
  }
  return (
    <div className="map-thumb map-thumb-placeholder" aria-hidden="true">
      <MapIcon size={26} />
    </div>
  );
}

// ─── List (table) view ──────────────────────────────────────────────────────
function MapsTable({ maps, onOpen, onRename, onDelete }) {
  if (maps.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <h3>No maps yet</h3>
          <p>Create your first map to get started.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="table-wrap" style={{ border: 'none' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Client</th>
              <th style={{ textAlign: 'right' }}>Groups</th>
              <th style={{ textAlign: 'right' }}>Areas</th>
              <th style={{ textAlign: 'right' }}>Datasets</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {maps.map((map) => (
              <tr key={map.id} className="is-clickable" onClick={() => onOpen(map)}>
                <td>
                  <span className="map-link">
                    <LegendDots colors={map.group_colors} className="mini-dots" />
                    {map.name || 'Untitled map'}
                  </span>
                </td>
                <td>
                  <span className="badge">{map.client_name || 'Unassigned'}</span>
                </td>
                <td style={{ textAlign: 'right' }}>{map.group_count ?? 0}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(map.area_count ?? 0)}</td>
                <td style={{ textAlign: 'right' }}>{map.dataset_count ?? 0}</td>
                <td>{relativeTime(map.updated_at)}</td>
                <td>
                  <div className="row-actions">
                    <button
                      className="btn-icon"
                      title="Rename"
                      onClick={(e) => onRename(map, e)}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      className="btn-icon"
                      title="Delete"
                      onClick={(e) => onDelete(map, e)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Loading skeleton ───────────────────────────────────────────────────────
function DashboardSkeleton() {
  return (
    <div className="maps-grid" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="card map-card">
          <div className="skeleton" style={{ height: 140, borderRadius: 0 }} />
          <div className="map-card-body">
            <div className="skeleton skeleton-line w-70" />
            <div className="skeleton skeleton-line w-60" />
          </div>
        </div>
      ))}
    </div>
  );
}
